import { createSelector } from '@reduxjs/toolkit';
import _difference from 'lodash-es/difference.js';

import { isolatedDialog, isolatedHeader } from 'Addons/fitmentSearch/isolatedKeys.ts';
import { selectedVehicleSelector } from 'Core/reducers/fitmentSearch/storage.ts';
import { FacetValue, Vehicle } from 'Models/index.ts';
import { createFacetCollectionSelector, createIsolatedSelectorCreator } from 'Modules/selectors.js';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig.js';
import requestConfig from 'Models/uiConfig/requestConfig.js';
import { equalBy } from 'Utils/array.js';
import { fitmentSearchSelector } from './fitmentSearch.js';

function createRootSelector(isolatedKey = null) {
  return isolatedKey ? (state) => fitmentSearchSelector(state)[isolatedKey] : fitmentSearchSelector;
}

const createFitmentSearchBaseFieldsSelector = createIsolatedSelectorCreator(
  createRootSelector,
  ([rootSelector]) =>
    (state) => {
      return rootSelector(state).fields.base;
    },
);

export const createFitmentSearchFieldsSelector = createIsolatedSelectorCreator(
  createRootSelector,
  ([rootSelector]) =>
    (state) => [...rootSelector(state).fields.base, ...rootSelector(state).fields.extra],
);

export const createFitmentSearchExtraFieldsSelector = createIsolatedSelectorCreator(
  createRootSelector,
  ([rootSelector]) =>
    (state) =>
      rootSelector(state).fields.extra,
);

const createIsolatedRequestSelector = createIsolatedSelectorCreator(
  createRootSelector,
  ([rootSelector]) =>
    (state) =>
      rootSelector(state).request,
);

export const createFitmentSearchRequestSelectionSelector = createIsolatedSelectorCreator(
  createIsolatedRequestSelector,
  ([requestSelector]) =>
    (state) =>
      requestSelector(state).selection,
);

const createIsolatedResponseSelector = createIsolatedSelectorCreator(
  createRootSelector,
  ([rootSelector]) =>
    (state) =>
      rootSelector(state).response,
);

export const createFitmentSearchResponseAllFacetsSelector = createIsolatedSelectorCreator(
  createIsolatedResponseSelector,
  (selectors) => createFacetCollectionSelector(selectors, (response) => response.facets),
);

export const createFilteredFitmentSearchResponseFieldsSelector = createIsolatedSelectorCreator(
  createFitmentSearchResponseAllFacetsSelector,
  createFitmentSearchBaseFieldsSelector,
  createFitmentSearchExtraFieldsSelector,
  createFitmentSearchFieldsSelector,
  (selectors, isolatedKey) =>
    createSelector(selectors, (facets, fitmentBaseFields, fitmentExtraFields, fitmentFields) => {
      const allBaseFieldsSelected = fitmentBaseFields.every((field) => {
        const facet = facets.get(field);
        return !facet || facet.selection?.length;
      });
      return facets
        .filter((facet) => {
          // do not remove any base field
          if (fitmentBaseFields.includes(facet.field)) {
            return true;
          }

          // do not remove any extra field if there is only one base field
          if (
            fitmentBaseFields.length === 1 &&
            fitmentExtraFields.length &&
            fitmentFields.includes(facet.field)
          ) {
            return true;
          }

          // remove the extra field in the following cases
          if (
            (!fitmentSearchConfig.isExtraFieldsAlwaysVisible &&
              (fitmentSearchConfig.isOnCategorySelectionPage ||
                isolatedKey === isolatedHeader ||
                (isolatedKey === isolatedDialog && !requestConfig.hasSearchResults))) ||
            !allBaseFieldsSelected ||
            !fitmentFields.includes(facet.field)
          ) {
            return false;
          }

          // do not remove selected extra fields
          if (facet.selection.length) {
            return true;
          }

          // do not remove extra fields if:
          // partial mode enabled
          // or in VerifyFitment widget
          // or if there is values and not only one Universal value
          return fitmentSearchConfig.isVehicleWidgetInPartialMode || facet.values.length > 0;
        })
        .map((facet) => facet.field);
    }),
);

export const createRequiredFitmentSearchFieldsSelector = createIsolatedSelectorCreator(
  createFilteredFitmentSearchResponseFieldsSelector,
  createFitmentSearchBaseFieldsSelector,
  (selectors) =>
    createSelector(selectors, (responseFields, baseFields) => {
      const requiredFields = [baseFields, responseFields].reduce((requiredFields, fitmentFields) =>
        requiredFields.filter((requiredField) => fitmentFields.includes(requiredField)),
      );
      return requiredFields.length ? requiredFields : null;
    }),
);

export const createFitmentSearchResponseFacetsSelector = createIsolatedSelectorCreator(
  createFitmentSearchResponseAllFacetsSelector,
  createFilteredFitmentSearchResponseFieldsSelector,
  (selectors) =>
    createFacetCollectionSelector(selectors, (facets, fields) =>
      facets.filter((f) => fields.includes(f.field)),
    ),
);

export const createFitmentSearchResponseSelectionSelector = createIsolatedSelectorCreator(
  createFitmentSearchResponseFacetsSelector,
  (selectors) => createSelector(selectors, (facets) => facets.selection),
);

export const createFitmentSearchSelectionChangedSelector = createIsolatedSelectorCreator(
  createFitmentSearchRequestSelectionSelector,
  createFitmentSearchResponseSelectionSelector,
  (selectors) =>
    createSelector(
      selectors,
      (requestSelection, responseSelection) =>
        !equalBy(requestSelection, responseSelection, FacetValue.termKey),
    ),
);

export const createAllBaseFieldsHaveSelectionSelector = createIsolatedSelectorCreator(
  createFitmentSearchBaseFieldsSelector,
  createFitmentSearchResponseSelectionSelector,
  ([baseFieldsSelector, selectionSelector]) =>
    (state) => {
      const baseFields = baseFieldsSelector(state);
      return !(
        baseFields &&
        _difference(
          baseFields,
          selectionSelector(state).map((sel) => sel.field),
        ).length
      );
    },
);

export const createAllRequiredFieldsHaveSelectionSelector = createIsolatedSelectorCreator(
  createRequiredFitmentSearchFieldsSelector,
  createFitmentSearchResponseSelectionSelector,
  ([requiredFieldsSelector, selectionSelector]) =>
    (state) => {
      const requiredFields = requiredFieldsSelector(state);
      return !(
        requiredFields &&
        _difference(
          requiredFields,
          selectionSelector(state).map((sel) => sel.field),
        ).length
      );
    },
);

export const createResponseVehicleSelector = createIsolatedSelectorCreator(
  createFitmentSearchResponseSelectionSelector,
  createFitmentSearchFieldsSelector,
  (selectors) =>
    createSelector(selectors, (selection, fitmentFields) => new Vehicle(selection, fitmentFields)),
);

export const createIsServerChangedVehicleSelector = createIsolatedSelectorCreator(
  createResponseVehicleSelector,
  createAllRequiredFieldsHaveSelectionSelector,
  createFitmentSearchFieldsSelector,
  ([responseVehicleSelector, isAllRequiredFieldHasSelectionSelector, fitmentFieldsSelector]) =>
    createSelector(
      selectedVehicleSelector,
      responseVehicleSelector,
      isAllRequiredFieldHasSelectionSelector,
      fitmentFieldsSelector,
      (_, fields) => fields,
      (selectedVehicle, responseVehicle, isAllRequiredFieldHasSelection, allFields, widgetFields) => {
        const fitmentFields = widgetFields?.length ? widgetFields : allFields;
        const selectedFilteredVehicle = new Vehicle(selectedVehicle.fieldMap, fitmentFields);
        const responseFilteredVehicle = new Vehicle(responseVehicle.fieldMap, fitmentFields);

        return (
          selectedFilteredVehicle.notNull &&
          !selectedFilteredVehicle.equals(responseFilteredVehicle) &&
          (!selectedFilteredVehicle.isMoreSpecificThan(responseFilteredVehicle) ||
            !isAllRequiredFieldHasSelection)
        );
      },
    ),
);
